import type { Hex, Lines, MemberId, Widget } from '../contract.js';
import { copy } from '../copy.js';
import { dayStatus, isYou, memberOf } from '../state/lookup.js';
import type { AppState } from '../state/types.js';
import type { DayVals } from './day.js';
import { pfpOfMember } from './picture.js';
import type { Pfp } from './picture.js';

/** An open post, ported from `personVals` (03 §3.6–3.8). */

export type SlideClass = '' | 'slL0' | 'slL1' | 'slR0' | 'slR1';

export interface PersonVals {
  /** The post is on screen: you are in a post, its author posted that day, and the day has loaded. */
  readonly show: boolean;
  readonly id: MemberId | null;
  readonly items: readonly Widget[];
  readonly lines: Lines | null;
  readonly color: Hex;
  /** The grid's clip: the author's square while opening or closing, the whole grid otherwise. */
  readonly clip: string;
  readonly clipTr: string;
  /** Slide class when cycling between posts. */
  readonly slide: SlideClass;
  readonly picture: Pfp;
  /** The centre picture: it flies from the author's square into the island. */
  readonly pTf: string; readonly pTr: string; readonly pR: string;
  readonly wob: '' | 'wobA' | 'wobB'; readonly wobDelay: string;
  readonly pAria: string;
  readonly arOp: number; readonly arTr: string; readonly arOff: boolean;
  readonly prevAria: string; readonly nextAria: string;
}

export function personVals(st: AppState, day: DayVals, reduced: boolean): PersonVals {
  const id = st.person, pp = st.pp, inPerson = st.view === 'person', entries = day.entries, n = entries.length;
  const idx = entries.findIndex((entry) => entry.memberId === id), entry = idx >= 0 ? entries[idx] : undefined;
  const m = memberOf(st, id), pre = pp === 'in-pre' || pp === 'out';
  const L = (idx >= 0 && day.layout[idx]) || { x: 686, y: 441 };
  const squareClip = `inset(${L.y - 236}px ${1000 - L.x - 68}px ${714 - L.y - 68}px ${L.x - 440}px round 6px)`;
  const neighbour = (dir: -1 | 1): { readonly name: string; readonly you: boolean } | null => {
    if (n < 2 || idx < 0) return null;
    const other = entries[(idx + dir + n) % n]?.memberId ?? null;
    return { name: memberOf(st, other).name, you: isYou(st, other) };
  };
  const prev = neighbour(-1), next = neighbour(1);
  return {
    show: inPerson && !!entry && dayStatus(st, st.date) === 'loaded',
    id, items: entry ? entry.post.items : [], lines: entry ? entry.post.lines || null : null, color: m.color,
    clip: pre ? squareClip : 'inset(0px 0px 0px 0px round 0px)',
    clipTr: pp === 'in' ? 'clip-path 620ms cubic-bezier(.7,0,.2,1) 60ms' : pp === 'out' ? 'clip-path 460ms cubic-bezier(.4,0,.2,1)' : 'none',
    slide: reduced || !st.slide ? '' : (`sl${st.slide < 0 ? 'L' : 'R'}${st.cyc % 2}` as SlideClass),
    picture: pfpOfMember(st, m),
    pTf: pre ? `translate(${L.x - 698}px, ${L.y - 814}px) scale(1.545)` : 'none',
    pTr: pp === 'in' ? 'transform 700ms cubic-bezier(.34,1.3,.52,1)' : pp === 'out' ? 'transform 460ms cubic-bezier(.4,0,.2,1)' : 'none',
    pR: pre ? '4px' : '50%',
    wob: reduced || pre ? '' : st.cyc % 2 ? 'wobB' : 'wobA', wobDelay: pp === 'in' ? '460ms' : '0ms',
    pAria: copy.hub.openProfile(m.name, isYou(st, id)),
    arOp: pre ? 0 : n < 2 ? 0.3 : 1,
    arTr: pp === 'in' ? 'opacity 300ms ease 420ms' : pp === 'out' ? 'opacity 160ms ease' : 'opacity 200ms ease',
    arOff: n < 2 || pre,
    prevAria: prev ? copy.hub.previous(prev.name, prev.you) : copy.hub.previousPerson,
    nextAria: next ? copy.hub.next(next.name, next.you) : copy.hub.nextPerson,
  };
}
